import { Amplify } from "aws-amplify";

/**
 * Tells Amplify which Cognito pool it is talking to.
 *
 * Amplify keeps this in module state, and every `aws-amplify/auth` call reads
 * it. Until 2026-09-22 the only place that set it was a `useEffect` inside
 * `SignUp`, so anyone who opened `/sign-in-link` directly — which is exactly
 * what the funnel's confirmation email tells a new customer to do — hit
 * Amplify's "Auth UserPool not configured" before a single request left the
 * browser. Those accounts have no password, so that route is their only way in
 * (stentor-client #5459).
 *
 * Lives here rather than at app bootstrap on purpose: the auth calls
 * themselves go through it, so a future third caller cannot reintroduce the
 * bug by forgetting to configure first.
 */

/** Raised when the runtime config cannot name a pool. */
export class AmplifyConfigError extends Error {
    constructor(message: string) {
        super(message);
        // Without this, logs and stack traces read "Error:" and lose the type.
        this.name = "AmplifyConfigError";
    }
}

interface StudioAuthConfig {
    userPoolId?: string;
    userWebPoolClientId?: string;
    region?: string;
    /** The hosted-UI domain the federated providers redirect through. */
    domain?: string;
}

let configured = false;

/**
 * Configures Amplify's Cognito pool, once per page.
 *
 * Safe to call before every auth call. Throws `AmplifyConfigError` when the
 * runtime config is missing the pool or the client, rather than letting
 * Amplify raise its own error from somewhere inside `signIn` — this one names
 * what an operator has to fix.
 */
export function configureAmplifyAuth(): void {
    if (configured) {
        return;
    }

    const auth: StudioAuthConfig = window.xaStudioConfig?.auth ?? {};
    const userPoolId = auth.userPoolId;
    const userPoolClientId = auth.userWebPoolClientId;

    if (!userPoolId || !userPoolClientId) {
        throw new AmplifyConfigError(
            "Sign-in is not configured for this environment (missing auth.userPoolId or auth.userWebPoolClientId)."
        );
    }

    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId,
                userPoolClientId,
                // Carried over verbatim from the block this replaced in SignUp:
                // Google and Facebook sign-up go through the hosted UI, and the
                // redirect lists have to match what Cognito has registered.
                loginWith: {
                    oauth: {
                        domain: auth.domain,
                        scopes: ["email", "openid"],
                        responseType: "code",
                        redirectSignIn: [
                            "http://localhost:3000/authorize",
                            "https://stentor-api-devv2.auth.us-east-1.amazoncognito.com/oauth2/idpresponse",
                            "https://studio-xapp-aiv2.auth.us-east-1.amazoncognito.com/oauth2/idpresponse",
                            "https://auth.xapp.ai/oauth2/idpresponse",
                            "https://studio.xapp.ai/authorize",
                            // staging
                            "https://auth-stage.xapp.media/oauth2/idpresponse",
                        ],
                        redirectSignOut: ["http://localhost:3000/", "https://studio.xapp.ai/"],
                        providers: ["Google", "Facebook"],
                    },
                },
            },
        },
    });

    // Only after it worked. config.js can load late, and remembering a failure
    // would leave sign-in broken for the life of the page.
    configured = true;
}

/** Test seam: the flag is module state, which survives between tests. */
export function resetAmplifyAuthForTests(): void {
    configured = false;
}
